import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Header from "../components/Header";
import { autorizacaoObter, autorizacaoDecidir } from "../services/api";
import { AvisoEmail, useCampoEmail } from "../components/AvisoEmail";

// Página do link de AUTORIZAÇÃO do candidato (/autorizar/{token}) — sem conta e sem senha.
//
// Por que existe: o consentimento para a eaiRH acionar o contato do processo só era capturado no formulário
// do relato. Quem não marcou naquele momento ficava travado para sempre, e a empresa nunca era convidada a
// responder. Aqui a pessoa decide depois, em um clique, e a data fica gravada como prova.
//
// O texto é deliberadamente honesto sobre o que acontece: quem é acionado, o que a empresa vê e o que ela
// NÃO vê. Consentimento obtido com texto ambíguo não é consentimento.
export default function AutorizarPage() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [erro, setErro] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [feito, setFeito] = useState(null); // "autorizado" | "revogado"
  // Contato do processo. Só aparece para quem publicou o relato sem informar nenhum — que é a maioria.
  // Editável aqui e SÓ aqui: o texto do relato é imutável depois de publicado.
  const [c, setC] = useState({ contatoEmail: "", contatoWhatsapp: "" });
  const campoEmail = useCampoEmail(c.contatoEmail, (v) => setC((x) => ({ ...x, contatoEmail: v })));

  useEffect(() => {
    autorizacaoObter(token)
      .then((x) => {
        setD(x);
        setC({ contatoEmail: x.contatoEmail || "", contatoWhatsapp: x.contatoWhatsapp || "" });
      })
      .catch(() => setErro("invalido"));
  }, [token]);

  const decidir = async (autorizar) => {
    setSalvando(true); setErro(null);
    try {
      // Só manda o contato ao AUTORIZAR: quem está recusando não precisa ter o campo validado.
      const r = await autorizacaoDecidir(token, autorizar, autorizar ? c : undefined);
      setD((x) => ({ ...x, autorizado: r.autorizado, temEmail: r.temEmail, temWhatsapp: r.temWhatsapp }));
      setFeito(autorizar ? "autorizado" : "revogado");
    } catch (e) { setErro(e.message); }
    finally { setSalvando(false); }
  };

  // Sem e-mail nem WhatsApp não há o que autorizar — o botão fica travado e o texto abaixo explica por quê,
  // em vez de deixar a pessoa clicar e tomar um erro do servidor.
  const faltaContato = !!d?.precisaContato && !c.contatoEmail.trim() && !c.contatoWhatsapp.trim();

  if (erro === "invalido") return (
    <Tela>
      <Cartao>
        <p className="text-4xl mb-2">🔎</p>
        <h1 className="text-xl font-bold text-gray-900">Link inválido ou expirado</h1>
        <p className="mt-2 text-gray-600 text-sm">Se você recebeu este link do eaiRH, responda à mensagem que a gente reenvia.</p>
      </Cartao>
    </Tela>
  );

  if (!d) return <Tela><Cartao><p className="text-gray-500">Carregando…</p></Cartao></Tela>;

  const quem = d.contatoNome?.trim()
    ? d.contatoNome.trim()
    : d.precisaContato ? "o contato que você informar acima" : "a pessoa de contato que você informou";

  return (
    <Tela>
      <Cartao>
        {feito === "autorizado" ? (
          <>
            <p className="text-4xl mb-2">✅</p>
            <h1 className="text-xl font-bold text-gray-900">Autorização registrada. Obrigado!</h1>
            <p className="mt-2 text-gray-600 text-sm">
              A partir de agora podemos acionar {quem} sobre o seu relato na <strong>{d.empresa}</strong> e cobrar
              o retorno. Assim que houver resposta, você é avisado.
            </p>
            <button onClick={() => decidir(false)} disabled={salvando}
              className="mt-5 text-sm text-gray-500 underline hover:text-gray-700">
              Mudei de ideia — desautorizar
            </button>
          </>
        ) : feito === "revogado" ? (
          <>
            <p className="text-4xl mb-2">🔒</p>
            <h1 className="text-xl font-bold text-gray-900">Autorização retirada</h1>
            <p className="mt-2 text-gray-600 text-sm">
              Não vamos acionar ninguém em seu nome. Seu relato continua publicado normalmente — só a
              intermediação foi desligada.
            </p>
            <button onClick={() => decidir(true)} disabled={salvando}
              className="mt-5 text-sm text-primary underline">Autorizar novamente</button>
          </>
        ) : (
          <>
            <h1 className="text-2xl font-bold text-gray-900">
              {d.precisaContato ? <>Quer que a gente cobre uma resposta?</> : <>Podemos falar com a empresa por você?</>}
            </h1>
            <p className="mt-3 text-gray-700 text-sm">
              Você registrou um relato sobre o processo seletivo na <strong>{d.empresa}</strong>
              {d.vaga ? <> (vaga de <strong>{d.vaga}</strong>)</> : null}
              {d.precisaContato
                ? <>. Ele já está publicado — e continua publicado, com ou sem o que a gente pede aqui.</>
                : <> e informou um contato do processo.</>}
            </p>

            {/* Campos de contato. Só para quem publicou sem informar nenhum: sem e-mail ou WhatsApp de quem
                conduziu o processo, não existe para onde mandar o convite — o relato vira só registro público.
                A pergunta chega junto com o relato ainda fresco, quando a pessoa lembra com quem falou. */}
            {d.precisaContato && (
              <div className="mt-4 rounded-xl border border-orange-200 bg-orange-50 p-4 text-left">
                <p className="font-semibold text-gray-900">De quem era o processo?</p>
                <p className="text-sm text-gray-700 mt-1">
                  Basta um dos dois: o e-mail de onde saíram as mensagens que você recebeu, ou o WhatsApp de quem
                  te chamou. Pode ser da recrutadora ou do RH.
                </p>
                <label className="block mt-3">
                  <span className="text-sm font-medium text-gray-700">E-mail do RH ou da pessoa que te entrevistou</span>
                  <input type="email" inputMode="email" autoComplete="off" value={c.contatoEmail}
                    onChange={(e) => { setC({ ...c, contatoEmail: e.target.value }); campoEmail.aoDigitar(); }}
                    onBlur={campoEmail.aoSair}
                    placeholder="recrutamento@empresa.com.br"
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary outline-none" />
                  <AvisoEmail {...campoEmail.aviso} />
                </label>
                <label className="block mt-3">
                  <span className="text-sm font-medium text-gray-700">WhatsApp <span className="font-normal text-gray-500">(opcional)</span></span>
                  <input type="tel" inputMode="tel" autoComplete="off" value={c.contatoWhatsapp}
                    onChange={(e) => setC({ ...c, contatoWhatsapp: e.target.value })}
                    placeholder="(11) 90000-0000"
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary outline-none" />
                </label>
                <p className="text-xs text-gray-500 mt-3">
                  Não sabe ou prefere não informar? Tudo bem — seu relato continua no ar do mesmo jeito.
                </p>
              </div>
            )}

            <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700 text-left">
              <p className="font-semibold text-gray-900 mb-2">O que acontece se você autorizar</p>
              <ul className="space-y-1.5">
                <li>• Enviamos <strong>uma mensagem</strong> a {quem} convidando a empresa a responder publicamente.</li>
                <li>• A empresa responde por um link, <strong>sem criar conta</strong>, e a resposta passa por moderação.</li>
                <li>• Se ninguém responder no prazo, <strong>a gente cobra de novo</strong>.</li>
                <li>• Seu <strong>e-mail e telefone não são compartilhados</strong> — a conversa passa pelo eaiRH.</li>
              </ul>
              <p className="font-semibold text-gray-900 mt-4 mb-1">Se você não autorizar</p>
              <p>Seu relato <strong>continua publicado do mesmo jeito</strong>. A gente só não aciona ninguém em seu nome.</p>
            </div>

            {erro && <p role="alert" className="text-red-600 text-sm mt-3">{erro}</p>}

            <div className="mt-5 flex flex-col sm:flex-row gap-2 justify-center">
              <button onClick={() => decidir(true)} disabled={salvando || faltaContato}
                className="rounded-xl bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white font-semibold px-6 py-3">
                {salvando ? "Registrando…" : d.precisaContato ? "Pode cobrar a empresa" : "Sim, pode falar com a empresa"}
              </button>
              <button onClick={() => decidir(false)} disabled={salvando}
                className="rounded-xl border border-gray-300 hover:bg-gray-50 font-semibold px-6 py-3">
                Prefiro que não
              </button>
            </div>
            {faltaContato && (
              <p className="mt-3 text-xs text-gray-500">Preencha o e-mail ou o WhatsApp acima para liberar o botão.</p>
            )}
            <p className="mt-3 text-[11px] text-gray-400">Você pode mudar de ideia quando quiser, por este mesmo link.</p>
          </>
        )}
      </Cartao>
    </Tela>
  );
}

function Tela({ children }) {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-2xl mx-auto px-4 py-10">{children}</main>
    </div>
  );
}

function Cartao({ children }) {
  return <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm">{children}</div>;
}
